import { InjectionToken } from '@angular/core';

/**
 * Cross-shell route configuration for the manufacturing domain (BOM / material requirements).
 *
 * Different app shells mount these feature routes at different base paths — e.g.
 * zopup-main-app exposes them as top-level routes (`/material-requirements`) while
 * inflewz-main-app nests them under a `work-management` section
 * (`/work-management/material-requirements`). Components outside `zop-ux-manufacturer`
 * (e.g. zop-ux-project's project materials tab) that need to deep-link into these routes
 * must go through this token rather than hardcoding a path, so the same component works
 * unmodified in every app shell.
 */
export interface ZopManufacturingRouteConfig {
  /**
   * Base path for the material requirements feature routes, without a trailing slash.
   * e.g. '/material-requirements' or '/work-management/material-requirements'
   */
  materialRequirementsBasePath: string;
}

/**
 * Default manufacturing route configuration — matches zopup-main-app's top-level convention.
 * Apps that nest these routes differently (e.g. inflewz-main-app) must override via
 * `provideZopManufacturingRoutes`.
 */
export const DEFAULT_MANUFACTURING_ROUTE_CONFIG: ZopManufacturingRouteConfig = {
  materialRequirementsBasePath: '/material-requirements',
};

/**
 * Injection token for manufacturing route configuration.
 * Apps should provide their config using this token when their route table diverges
 * from the default (see `DEFAULT_MANUFACTURING_ROUTE_CONFIG`).
 *
 * @example
 * ```typescript
 * // In app.config.ts
 * provideZopManufacturingRoutes({
 *   materialRequirementsBasePath: '/work-management/material-requirements',
 * })
 * ```
 */
export const ZOP_MANUFACTURING_ROUTE_CONFIG = new InjectionToken<ZopManufacturingRouteConfig>(
  'ZOP_MANUFACTURING_ROUTE_CONFIG',
  {
    providedIn: 'root',
    factory: () => DEFAULT_MANUFACTURING_ROUTE_CONFIG,
  }
);
